import { useMemo } from "react"

import { ScreenName } from "~graphql/generated/types"
import { useOutfitterAssistantSuggestionsQuery } from "~graphql/outfitter-assistant.generated"

const FALLBACK_SCREEN_NAME = ScreenName.General

const SCREEN_NAME_ALIASES = {
    AVAILABILITY_CALENDAR: ScreenName.Calendar,
    CONTACTS: ScreenName.Clients,
    DEFAULT_AVAILABILITY_RULES: ScreenName.Calendar,
    EDIT_FORM: ScreenName.Forms,
    HOME_NEW: ScreenName.Home,
    LEGACY_CLIENTS: ScreenName.Clients,
    MANAGE_LISTINGS: ScreenName.Listings,
    NEW_DEFAULT_AVAILABILITY_RULE: ScreenName.Calendar,
    NEW_FORM: ScreenName.Forms,
    REPORTING_BUSINESS_PERFORMANCE: ScreenName.Reporting,
    REPORTING_PAYMENTS: ScreenName.Reporting,
    REPORTING_PAYOUTS: ScreenName.Reporting,
    REPORTING_UNPAID_BALANCES: ScreenName.Reporting,
    WEBSITE_BUILDER: ScreenName.WebBuilder,
} as const satisfies Record<string, ScreenName>
const SCREEN_NAME_VALUES: readonly string[] = Object.values(ScreenName)

interface UseOutfitterAssistantSuggestionsParams {
    readonly assistantContext: Record<string, unknown>
    readonly shouldSkip?: boolean
}

export default function useOutfitterAssistantSuggestions({
    assistantContext,
    shouldSkip = false,
}: UseOutfitterAssistantSuggestionsParams) {
    const screenName = useMemo(
        () => getSuggestionScreenName(assistantContext),
        [assistantContext]
    )
    const { data, previousData, loading, error } =
        useOutfitterAssistantSuggestionsQuery({
            variables: { input: { screen_name: screenName } },
            skip: shouldSkip,
        })

    return {
        suggestions:
            data?.outfitterAssistantSuggestions ??
            previousData?.outfitterAssistantSuggestions ??
            [],
        isLoading: loading,
        error,
    }
}

function getSuggestionScreenName(
    assistantContext: Record<string, unknown>
): ScreenName {
    const screenName = getAssistantContextScreenName(assistantContext)
    if (!screenName) return FALLBACK_SCREEN_NAME

    return toScreenName(screenName) ?? FALLBACK_SCREEN_NAME
}

function getAssistantContextScreenName(
    assistantContext: Record<string, unknown>
): string | null {
    const page = assistantContext.page
    if (!page || typeof page !== "object" || !("screenName" in page)) {
        return null
    }

    return typeof page.screenName === "string" ? page.screenName : null
}

function toScreenName(screenName: string): ScreenName | null {
    if (isScreenName(screenName)) return screenName

    if (isScreenNameAlias(screenName)) return SCREEN_NAME_ALIASES[screenName]

    return null
}

function isScreenName(value: string): value is ScreenName {
    return SCREEN_NAME_VALUES.includes(value)
}

function isScreenNameAlias(
    value: string
): value is keyof typeof SCREEN_NAME_ALIASES {
    return Object.prototype.hasOwnProperty.call(SCREEN_NAME_ALIASES, value)
}
